import { DefaultLogger, SafeShell } from '../../../core';
import type { PageResolution, PageSignalPage, ResolvePage } from './types';

/**
 * 页面信息解析器：负责路径规范化、参数白名单过滤、来源页与页面名称解析。
 * 纯同步逻辑，无任何运行时 / 框架依赖。
 */
export class PageInfoParser {
  constructor(private safeShell: SafeShell = new SafeShell(new DefaultLogger())) {}

  /**
   * 规范化路径：去首尾斜杠并剥离 query / hash，统一 routeRules 的匹配键。
   */
  normalize(path: string): string {
    if (typeof path !== 'string') return '';
    let normalized = path.trim();
    const boundary = normalized.search(/[?#]/);
    if (boundary >= 0) normalized = normalized.slice(0, boundary);
    return normalized.replace(/^\/+/, '').replace(/\/+$/, '');
  }

  /**
   * 按白名单过滤 query 并序列化为 query 片段（含 '?' 前缀，空则返回 ''）。
   * 未传入 keys 表示默认不透传任何参数。
   */
  buildQueryString(query: Record<string, unknown> | undefined, keys?: readonly string[]): string {
    if (!query || typeof query !== 'object') return '';
    const allowed = keys ?? [];
    const pairs: string[] = [];
    for (const [key, value] of Object.entries(query)) {
      if (!allowed.includes(key)) continue;
      if (value === undefined || value === null) continue;
      pairs.push(`${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`);
    }
    return pairs.length > 0 ? `?${pairs.join('&')}` : '';
  }

  /** 组合规范化路径与白名单参数，输出为 _currentURL 的语义来源。 */
  buildUrl(path: string, query?: Record<string, unknown>, keys?: readonly string[]): string {
    return `${this.normalize(path)}${this.buildQueryString(query, keys)}`;
  }

  /**
   * 调用宿主解析器并安全降级：解析器抛异常或返回非法结构时返回空结果。
   */
  resolve(page: PageSignalPage, resolver?: ResolvePage): PageResolution {
    if (typeof resolver !== 'function') return {};
    return this.safeShell.run(() => {
      const raw = resolver(page);
      const result: PageResolution = {};
      if (raw && typeof raw === 'object') {
        if (typeof raw.name === 'string' && raw.name) result.name = raw.name;
        if (raw.properties && typeof raw.properties === 'object') result.properties = raw.properties;
      }
      return result;
    }, {}, 'PageView.resolvePage');
  }

  /** 组装 PV 事件属性：路径/来源页/页面名称/自定义属性，保证不携带内部标识。 */
  buildProperties(
    page: PageSignalPage,
    referrer: string | undefined,
    keys?: readonly string[],
    resolver?: ResolvePage,
  ): Record<string, unknown> {
    const properties: Record<string, unknown> = { pageUrl: this.buildUrl(page.path, page.query, keys) };
    if (referrer) properties.referrer = referrer;
    const resolution = this.resolve(page, resolver);
    if (resolution.name) properties.pageName = resolution.name;
    Object.assign(properties, resolution.properties);
    Object.assign(properties, page.properties);
    return properties;
  }
}